// Heatmap API hook + response types for the admin /staff/heatmap page.
//
// Snapshot-by-default; LIVE mode flips refetchInterval on so the page
// stays fresh while the operator is actively watching. No mutations
// happen through this hook — the heatmap is a viewer, any "kick this
// phone" action lives on a different endpoint so a dashboard refresh
// can't accidentally trigger one.

import { useQuery } from '@tanstack/react-query'
import { api } from './client'

/** Structured trouble-signal codes — the UI maps each to an icon. */
export type ReasonType =
  | 'offline'
  | 'no_sim'
  | 'low_battery'
  | 'weak_signal'
  | 'failed_calls'
  | 'stale'
  | 'no_perm'

export type PhoneState = 'idle' | 'active' | 'trouble'

export interface HeatmapReason {
  type: ReasonType
  /** Russian label — card tooltip / accessibility. */
  label: string
  /** pct for low_battery, consecutive-fail count for failed_calls. */
  value: number | null
}

/** One phone on the board. The whole fleet is returned — every phone
 * keeps a fixed grid slot. `state` decides which fields are populated. */
export interface HeatmapPhone {
  id: string
  name: string
  sim: string
  state: PhoneState
  battery_pct: number | null
  signal_bars: number | null
  /** state==='active': real call duration anchored on in_call_since. */
  in_call_seconds: number | null
  stale: boolean
  /** state==='trouble': */
  trouble_score: number | null
  reasons: HeatmapReason[]
}

export interface HeatmapResponse {
  phones: HeatmapPhone[]
  server_time: string
  /** False when relay heartbeat is stale; the whole fleet then reads as
   * offline (PhoneService.list overlay) and the UI shows a banner. */
  relay_online: boolean
}

/**
 * Fetch the heatmap snapshot. When `live=true`, polls every 3 seconds.
 * The interval is a first-iteration guess — bump to 5s if the endpoint
 * p95 latency creeps past ~1s once the real fleet is observed.
 */
export function useHeatmap({ live }: { live: boolean }) {
  return useQuery({
    queryKey: ['admin', 'heatmap'],
    queryFn: () => api.get<HeatmapResponse>('/api/v1/admin/heatmap/phones'),
    refetchInterval: live ? 3_000 : false,
    refetchIntervalInBackground: false,
  })
}
